import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AdminLayout from "./component/AdminLayout";
import Paging from '../../common/layouts/page/Paging';

const AdminAnalytics = () => {

    // 로그 데이터
    const [socialSignupLogs, setSocialSignupLogs] = useState<any[]>([]);
    const [realNameLogs, setRealNameLogs] = useState<any[]>([]);

    // 실명인증 탭 및 신규 API 데이터 상태
    const [realNameTab, setRealNameTab] = useState<'ALL' | 'ATTEMPTS' | 'SEARCH'>('ALL');
    const [attemptsLogs, setAttemptsLogs] = useState<any[]>([]);
    const [attemptsPage, setAttemptsPage] = useState<number>(1);
    const [attemptsTotalPages, setAttemptsTotalPages] = useState<number>(1);
    const [searchEmail, setSearchEmail] = useState<string>('');
    const [searchResultLogs, setSearchResultLogs] = useState<any[]>([]);

    // API New Data
    const [summary, setSummary] = useState<any>(null);
    const [topBooks, setTopBooks] = useState<any[]>([]);
    const [topAuthors, setTopAuthors] = useState<any[]>([]);

    // 토큰을 ref로 관리 — setState 하지 않아 리렌더 없음
    const adminTokenRef = useRef<string | null>(null);

    const getAdminToken = useCallback(async (): Promise<string | null> => {
        return sessionStorage.getItem('adminToken');
    }, []);

    const fetchSocialSignupLogs = useCallback(async () => {
        const token = await getAdminToken();
        if (!token) return;
        try {
            const response = await axios.post('/api/admin/users/social-signups/logs?limit=100', {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                setSocialSignupLogs(response.data);
            }
        } catch (error) {
            console.error('Error fetching social signup logs:', error);
        }
    }, [getAdminToken]);

    const fetchRealNameLogs = useCallback(async () => {
        const token = await getAdminToken();
        if (!token) return;
        try {
            const response = await axios.post('/api/admin/users/realname-verifications/logs?limit=100', {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                setRealNameLogs(response.data);
            }
        } catch (error) {
            console.error('Error fetching realname logs:', error);
        }
    }, [getAdminToken]);

    const fetchAttemptsLogs = useCallback(async (page: number) => {
        const token = await getAdminToken();
        if (!token) return;
        try {
            const response = await axios.post(`/api/admin/users/realname-verifications/attempts?page=${page}&size=10`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = response.data;
            if (data) {
                setAttemptsLogs(data.content || []);
                setAttemptsTotalPages(data.totalPages || 1);
            }
        } catch (error) {
            console.error('Error fetching attempts logs:', error);
        }
    }, [getAdminToken]);

    const fetchSearchLogs = useCallback(async (email: string) => {
        if (!email.trim()) return;
        const token = await getAdminToken();
        if (!token) return;
        try {
            const response = await axios.post(`/api/admin/users/realname-verifications/search?email=${encodeURIComponent(email)}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (Array.isArray(response.data)) {
                setSearchResultLogs(response.data);
            } else {
                setSearchResultLogs([]);
            }
        } catch (error) {
            console.error('Error searching realname logs:', error);
            setSearchResultLogs([]);
        }
    }, [getAdminToken]);

    useEffect(() => {
        fetchSocialSignupLogs();
        fetchRealNameLogs();
    }, [fetchSocialSignupLogs, fetchRealNameLogs]);

    useEffect(() => {
        if (realNameTab === 'ATTEMPTS') {
            fetchAttemptsLogs(attemptsPage);
        }
    }, [realNameTab, attemptsPage, fetchAttemptsLogs]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            const token = await getAdminToken();
            if (!token) return;
            try {
                const [sumRes, booksRes, authorsRes] = await Promise.all([
                    axios.post('/api/admin/dashboard/summary', {}, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.post('/api/admin/dashboard/top-books', {}, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.post('/api/admin/dashboard/top-authors', {}, { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setSummary(sumRes.data);
                if (Array.isArray(booksRes.data)) setTopBooks(booksRes.data);
                if (Array.isArray(authorsRes.data)) setTopAuthors(authorsRes.data);
            } catch (error) {
                console.error("Failed to fetch dashboard summary and top lists", error);
            }
        };
        fetchDashboardData();
    }, [getAdminToken]);

    const formatSecondsToMinSec = (seconds: number) => {
        if (!seconds || isNaN(seconds)) return '0초';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        if (m > 0) return `${m}분 ${s}초`;
        return `${s}초`;
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const dd = String(date.getDate()).padStart(2, '0');
            const hh = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            const ss = String(date.getSeconds()).padStart(2, '0');
            return `${yyyy}.${mm}.${dd} ${hh}:${min}:${ss}`;
        } catch {
            return dateStr;
        }
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone) return '-';
        const cleaned = phone.replace(/[^0-9]/g, '');
        if (cleaned.length === 11) {
            return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
        } else if (cleaned.length === 10) {
            if (cleaned.startsWith('02')) {
                return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
            } else {
                return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
            }
        } else if (cleaned.length === 9) {
            return cleaned.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
        }
        return phone;
    };

    return (
        <AdminLayout
            pageTitle="Analytics"
            breadcrumbs={[
                { label: 'Home', link: '/admin' },
                { label: 'Analytics', active: true }
            ]}
        >
            <div className="main-content">
                {summary && (
                    <div className="row mb-4">
                        {[
                            { title: '방문 회원 수', data: summary.visitors },
                            { title: '작가 등록 회원 수', data: summary.registrations },
                            { title: '접속자 수', data: summary.connections },
                            { title: '평균 체류시간', data: summary.avgDurationMin, isDuration: true }
                        ].map((stat, idx) => (
                            <div key={idx} className="col-12 col-sm-6 col-xl-3 mb-4 mb-xl-0">
                                <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] h-100">
                                    <div className="card-body p-4">
                                        <h6 className="text-muted fw-medium mb-1 fs-12 text-[#64748b]">{stat.title}</h6>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h3 className="fw-bold mb-0 text-dark">
                                                {stat.isDuration ? formatSecondsToMinSec(stat.data?.today || 0) : (stat.data?.today?.toLocaleString() ?? 0)}
                                            </h3>
                                            <div 
                                                className="d-flex align-items-center px-2.5 py-1 rounded-pill small fw-bold" 
                                                style={{ 
                                                    backgroundColor: stat.data?.diff >= 0 ? '#d1fae5' : '#fee2e2', 
                                                    color: stat.data?.diff >= 0 ? '#065f46' : '#991b1b' 
                                                }}
                                            >
                                                <i className={`feather-${stat.data?.diff >= 0 ? 'arrow-up-right' : 'arrow-down-right'} me-1`}></i>
                                                <span>{Math.abs(stat.data?.diff || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="row">
                    {/* 최근 소셜 가입 로그 */}
                    <div className="col-12 mb-4">
                        <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden">
                            <div className="card-header bg-white border-0 pt-4 pb-3 d-flex justify-content-between align-items-center px-4">
                                <h5 className="card-title fw-bold text-dark mb-0">본 가입 로그 (소셜 가입)</h5>
                                <button 
                                    className="btn btn-xs btn-outline-secondary rounded px-3 py-1.5 fs-11 fw-bold" 
                                    onClick={fetchSocialSignupLogs}
                                >
                                    <span className="me-1">↻</span> 새로고침
                                </button>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '150px' }}>
                                    <table className="table table-hover align-middle mb-0" style={{ minWidth: '700px', tableLayout: 'fixed' }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '280px' }}>EMAIL</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '180px' }}>PROVIDER</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '240px' }}>가입 일시</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {socialSignupLogs.length > 0 ? socialSignupLogs.map((log, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 fw-medium text-dark">{log.userEmail}</td>
                                                    <td className="px-4 py-3">
                                                        {log.platform === 'kakao' ? (
                                                            <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#fde047', color: '#713f12' }}>
                                                                KAKAO
                                                            </span>
                                                        ) : log.platform === 'naver' ? (
                                                            <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#bbf7d0', color: '#14532d' }}>
                                                                NAVER
                                                            </span>
                                                        ) : (
                                                            <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#bfdbfe', color: '#1e3a8a' }}>
                                                                {log.platform.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-muted small">
                                                        {formatDate(log.signedUpAt)}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan={3} className="text-center py-5 text-muted">로그 데이터가 없습니다.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 실명인증 로그 */}
                    <div className="col-12 mb-4">
                        <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden">
                            <div className="card-header bg-white border-0 pt-4 pb-3 px-4">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <h5 className="card-title fw-bold text-dark mb-0">실명인증 로그</h5>
                                        <div className="btn-group border rounded-[8px] overflow-hidden" role="group">
                                            <button 
                                                type="button" 
                                                className={`btn btn-sm px-3 py-1.5 fw-semibold ${realNameTab === 'ALL' ? 'btn-primary text-white' : 'btn-light'}`}
                                                onClick={() => setRealNameTab('ALL')}
                                                style={{ fontSize: '12px' }}
                                            >
                                                전체 시도 로그
                                            </button>
                                            <button 
                                                type="button" 
                                                className={`btn btn-sm px-3 py-1.5 fw-semibold ${realNameTab === 'ATTEMPTS' ? 'btn-primary text-white' : 'btn-light'}`}
                                                onClick={() => {
                                                    setRealNameTab('ATTEMPTS');
                                                    setAttemptsPage(1);
                                                    fetchAttemptsLogs(1);
                                                }}
                                                style={{ fontSize: '12px' }}
                                            >
                                                사용자별 최신 결과
                                            </button>
                                            <button 
                                                type="button" 
                                                className={`btn btn-sm px-3 py-1.5 fw-semibold ${realNameTab === 'SEARCH' ? 'btn-primary text-white' : 'btn-light'}`}
                                                onClick={() => setRealNameTab('SEARCH')}
                                                style={{ fontSize: '12px' }}
                                            >
                                                이력 검색
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {realNameTab === 'ALL' && (
                                        <button 
                                            className="btn btn-xs btn-outline-secondary rounded px-3 py-1.5 fs-11 fw-bold align-self-start align-self-md-auto" 
                                            onClick={fetchRealNameLogs}
                                        >
                                            <span className="me-1">↻</span> 새로고침
                                        </button>
                                    )}

                                    {realNameTab === 'ATTEMPTS' && (
                                        <button 
                                            className="btn btn-xs btn-outline-secondary rounded px-3 py-1.5 fs-11 fw-bold align-self-start align-self-md-auto" 
                                            onClick={() => fetchAttemptsLogs(attemptsPage)}
                                        >
                                            <span className="me-1">↻</span> 새로고침
                                        </button>
                                    )}

                                    {realNameTab === 'SEARCH' && (
                                        <form onSubmit={(e) => { e.preventDefault(); fetchSearchLogs(searchEmail); }} className="d-flex align-items-center gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="이메일 검색" 
                                                className="form-control form-control-sm rounded px-3 py-1.5"
                                                style={{ fontSize: '12px', width: '220px', borderColor: '#e2e8f0' }}
                                                value={searchEmail}
                                                onChange={(e) => setSearchEmail(e.target.value)}
                                            />
                                            <button type="submit" className="btn btn-sm btn-primary rounded px-3 py-1.5 fw-bold text-white" style={{ fontSize: '12px', backgroundColor: '#6366f1', borderColor: '#6366f1' }}>
                                                검색
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '180px' }}>
                                    <table className="table table-hover align-middle mb-0" style={{ minWidth: '1750px', tableLayout: 'fixed' }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '200px' }}>EMAIL</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '90px' }}>성함</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '130px' }}>전화번호</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '90px' }}>시도 횟수</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '80px' }}>결과</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '220px' }}>상세 메시지</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '160px' }}>포트원 UID</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '110px' }}>사유 코드</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '110px' }}>포트원 상태</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '200px' }}>PG 거래 ID</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '160px' }}>포트원 인증 시각</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '160px' }}>최근 인증 시각</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const activeLogs = realNameTab === 'ALL' ? realNameLogs 
                                                                 : realNameTab === 'ATTEMPTS' ? attemptsLogs 
                                                                 : searchResultLogs;
                                                return activeLogs.length > 0 ? activeLogs.map((log, idx) => (
                                                    <tr key={idx}>
                                                        <td className="px-4 py-3 fw-medium text-dark">{log.email}</td>
                                                        <td className="px-4 py-3 text-dark fw-bold">{log.userName || '-'}</td>
                                                        <td className="px-4 py-3 text-muted small">{formatPhoneNumber(log.userPhone)}</td>
                                                        <td className="px-4 py-3">
                                                             <span className="badge rounded px-2.5 py-1.5 fs-11 fw-bold" style={{ backgroundColor: '#f1f5f9', color: '#475569' }}>
                                                                 {log.attemptCount}회
                                                             </span>
                                                         </td>
                                                         <td className="px-4 py-3">
                                                             {log.resultCode === 'SUCCESS' ? (
                                                                 <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#d1fae5', color: '#065f46' }}>성공</span>
                                                             ) : log.resultCode === 'INCOMPLETE' ? (
                                                                 <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#fef08a', color: '#854d0e' }}>미완료</span>
                                                             ) : (
                                                                 <span className="badge rounded-pill px-3 py-1.5 fs-11 border-0 fw-bold" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>실패</span>
                                                             )}
                                                         </td>
                                                        <td className="px-4 py-3 text-[#475569] small" style={{ whiteSpace: 'normal', minWidth: '180px', maxWidth: '300px' }}>
                                                            {log.message || '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-[#475569] small">{log.impUid || '-'}</td>
                                                        <td className="px-4 py-3 text-[#475569] small">{log.reasonCode || '-'}</td>
                                                        <td className="px-4 py-3 text-[#475569] small">{log.portoneStatus || '-'}</td>
                                                        <td className="px-4 py-3 text-[#475569] small">{log.pgTxId || '-'}</td>
                                                        <td className="px-4 py-3 text-muted small">{formatDate(log.portoneVerifiedAt)}</td>
                                                        <td className="px-4 py-3 text-muted small">
                                                            {formatDate(log.verifiedAt || log.lastVerifiedAt)}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan={12} className="text-center py-5 text-muted">로그 데이터가 없습니다.</td>
                                                    </tr>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {realNameTab === 'ATTEMPTS' && (
                                <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-4 px-4">
                                    <Paging totalPages={attemptsTotalPages} currentPage={attemptsPage} onPageChange={(p) => setAttemptsPage(p)} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* TOP 10 랭킹 */}
                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-3 px-4">
                                <h5 className="card-title fw-bold text-dark mb-0">작품 좋아요 TOP 10</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                    <table className="table table-hover align-middle mb-0" style={{ minWidth: '420px', tableLayout: 'fixed' }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '80px' }}>순위</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold">작품명</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-end" style={{ width: '130px' }}>좋아요 수</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topBooks.length > 0 ? topBooks.slice(0, 10).map((book, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 text-center">
                                                        {idx < 3 ? (
                                                            <span className="badge rounded px-2 py-1 fs-11 fw-bold w-100" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}>
                                                                {idx + 1}위
                                                            </span>
                                                        ) : (
                                                            <span className="badge rounded px-2 py-1 fs-11 w-100" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                                {idx + 1}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 fw-medium text-dark">{book.bookTitle || book.title || '-'}</td>
                                                    <td className="px-4 py-3 text-end">
                                                         <span className="badge rounded-pill px-3 py-1.5 fs-11 fw-bold" style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}>
                                                             ❤️ {book.likeCount || book.count || 0}
                                                         </span>
                                                     </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={3} className="text-center py-5 text-muted">리스트가 없습니다.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-6 mb-4">
                        <div className="card border border-[#f1f5f9] shadow-sm rounded-[16px] overflow-hidden h-100">
                            <div className="card-header bg-white border-0 pt-4 pb-3 px-4">
                                <h5 className="card-title fw-bold text-dark mb-0">작가 팔로우 TOP 10</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                    <table className="table table-hover align-middle mb-0" style={{ minWidth: '420px', tableLayout: 'fixed' }}>
                                        <thead style={{ backgroundColor: '#f8fafc' }}>
                                            <tr>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold" style={{ width: '80px' }}>순위</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold">작가명</th>
                                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-end" style={{ width: '130px' }}>팔로우 수</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topAuthors.length > 0 ? topAuthors.slice(0, 10).map((author, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-4 py-3 text-center">
                                                        {idx < 3 ? (
                                                            <span className="badge rounded px-2 py-1 fs-11 fw-bold w-100" style={{ backgroundColor: '#dcfce7', color: '#166534' }}>
                                                                {idx + 1}위
                                                            </span>
                                                        ) : (
                                                            <span className="badge rounded px-2 py-1 fs-11 w-100" style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                                {idx + 1}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 fw-medium text-dark">{author.authorName || author.name || '-'}</td>
                                                    <td className="px-4 py-3 text-end">
                                                        <span className="badge rounded-pill px-3 py-1.5 fs-11 fw-bold" style={{ backgroundColor: '#dcfce7', color: '#15803d' }}>
                                                            👤 {author.followCount || author.count || 0}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan={3} className="text-center py-5 text-muted">리스트가 없습니다.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminAnalytics;
